import {
  CREATIVE_TEMPLATE_ARTBOARD_PRESETS,
  CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID,
  CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_SIZE,
} from "../../../utils/constants";
import {
  applyCanvasBackgroundLayout,
  applySourceKindByName,
  canBeCanvasBackground,
  canvasBackgroundFit,
  countNodes,
  isCanvasBackgroundNode,
  listNodeNames,
  setRootCanvasBackground,
} from "./layerTree";
import { scaleHighlightMetrics } from "./textHighlights";
export {
  filterIncomingCarouselAssets,
  getReferenceAssetKey,
  isolateCarouselSlideGuides,
  referenceAssetIDIsShared,
} from "./slideGuides.js";

const PRESET_ORDER = ["square", "portrait", "story", "landscape"];
const RATIO_MATCH_THRESHOLD = 0.08;

export const listUncoveredNamesFromOtherArtboards = ({
  artboards = {},
  activeArtboardID,
}) => {
  const currentNames = new Set(
    listNodeNames({
      nodes: artboards[activeArtboardID]?.nodes || {},
    }).filter(Boolean)
  );
  const names = new Set();
  Object.entries(artboards || {}).forEach(([id, artboard]) => {
    if (id === activeArtboardID) return;
    listNodeNames({ nodes: artboard?.nodes || {} }).forEach((name) => {
      if (name && !currentNames.has(name)) names.add(name);
    });
  });
  return [...names].sort((left, right) => left.localeCompare(right));
};

export const listArtboardIDs = ({ artboards = {} }) => {
  const ids = Object.keys(artboards || {});
  const rank = (id) => {
    if (id === CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID) return 0;
    const presetIndex = PRESET_ORDER.indexOf(id);
    if (presetIndex >= 0) return 1 + presetIndex;
    return 10;
  };
  return ids.sort((left, right) => {
    const rankDiff = rank(left) - rank(right);
    if (rankDiff !== 0) return rankDiff;
    return left.localeCompare(right);
  });
};

export const getArtboard = ({ artboards = {}, artboardID }) =>
  artboards?.[artboardID] || null;

export const resolveActiveArtboardID = ({
  artboards = {},
  activeArtboardID,
}) => {
  if (activeArtboardID && artboards[activeArtboardID]) return activeArtboardID;
  if (artboards[CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID]) {
    return CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID;
  }
  return (
    listArtboardIDs({ artboards })[0] || CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID
  );
};

export const getArtboardRatio = ({ width, height }) => {
  if (!width || !height) return 1;
  return width / height;
};

export const matchPresetID = ({ width, height }) => {
  const ratio = getArtboardRatio({ width, height });
  let bestID = null;
  let bestDiff = Infinity;
  Object.values(CREATIVE_TEMPLATE_ARTBOARD_PRESETS).forEach((preset) => {
    const diff = Math.abs(ratio - preset.width / preset.height);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestID = preset.id;
    }
  });
  if (bestDiff > RATIO_MATCH_THRESHOLD) return null;
  return bestID;
};

export const resolveArtboardSizeFromDimensions = ({ width, height } = {}) => {
  const presetID = matchPresetID({ width, height });
  if (presetID && CREATIVE_TEMPLATE_ARTBOARD_PRESETS[presetID]) {
    const preset = CREATIVE_TEMPLATE_ARTBOARD_PRESETS[presetID];
    return { width: preset.width, height: preset.height };
  }
  return {
    width: CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_SIZE,
    height: CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_SIZE,
  };
};

export const getArtboardLabel = ({ artboard, artboardID } = {}) => {
  if (artboard?.label && artboard.label !== "source") return artboard.label;
  if (artboardID && CREATIVE_TEMPLATE_ARTBOARD_PRESETS[artboardID]) {
    return CREATIVE_TEMPLATE_ARTBOARD_PRESETS[artboardID].label;
  }
  const presetID = matchPresetID(artboard || {});
  if (presetID) return CREATIVE_TEMPLATE_ARTBOARD_PRESETS[presetID].label;
  if (artboard?.width && artboard?.height) {
    return `${artboard.width}×${artboard.height}`;
  }
  return artboardID || CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID;
};

export const getArtboardChipLabel = ({ artboardID, cardArtboard }) => {
  if (CREATIVE_TEMPLATE_ARTBOARD_PRESETS[artboardID]) {
    return CREATIVE_TEMPLATE_ARTBOARD_PRESETS[artboardID].label;
  }
  const cardArtboardMatches =
    Boolean(cardArtboard) &&
    (cardArtboard.id === artboardID ||
      (!cardArtboard.id &&
        artboardID === CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID));
  if (cardArtboardMatches) {
    return getArtboardLabel({ artboard: cardArtboard, artboardID });
  }
  return getArtboardLabel({ artboardID });
};

export const listUniqueArtboardFormatLabels = ({ artboards = {} }) => {
  const seenLabels = new Set();
  const labels = [];
  listArtboardIDs({ artboards }).forEach((artboardID) => {
    const label = getArtboardLabel({
      artboard: artboards[artboardID],
      artboardID,
    });
    if (!label || seenLabels.has(label)) return;
    seenLabels.add(label);
    labels.push(label);
  });
  return labels;
};

export const listAvailablePresets = ({ artboards = {} }) => {
  const usedPresetIDs = new Set();
  Object.entries(artboards || {}).forEach(([id, artboard]) => {
    if (CREATIVE_TEMPLATE_ARTBOARD_PRESETS[id]) usedPresetIDs.add(id);
    const matched = matchPresetID(artboard || {});
    if (matched) usedPresetIDs.add(matched);
  });
  return PRESET_ORDER.map(
    (id) => CREATIVE_TEMPLATE_ARTBOARD_PRESETS[id]
  ).filter((preset) => preset && !usedPresetIDs.has(preset.id));
};

const cloneTree = (value) => structuredClone(value ?? {});

const getCanvasFitTransform = ({ from, to }) => {
  const fromW = Math.max(1, Number(from?.width) || 1);
  const fromH = Math.max(1, Number(from?.height) || 1);
  const toW = Math.max(1, Number(to?.width) || 1);
  const toH = Math.max(1, Number(to?.height) || 1);
  const scale = Math.min(toW / fromW, toH / fromH);
  return {
    scale,
    offsetX: (toW - fromW * scale) / 2,
    offsetY: (toH - fromH * scale) / 2,
  };
};

const scalePx = ({ value, scale }) => Math.round((Number(value) || 0) * scale);

const scaleLayout = ({ layout, scale, offsetX = 0, offsetY = 0 }) => {
  if (!layout) return layout;
  return {
    ...layout,
    x: Math.round((layout.x || 0) * scale + offsetX),
    y: Math.round((layout.y || 0) * scale + offsetY),
    width: scalePx({ value: layout.width, scale }),
    height: scalePx({ value: layout.height, scale }),
  };
};

const scaleNodeMetrics = ({ node, scale }) => {
  const next = { ...node };
  const hasHighlightMetrics = Array.isArray(node.style?.highlights)
    ? node.style.highlights.some(
        (slot) =>
          slot?.strokeWidth != null ||
          slot?.backgroundPadding != null ||
          slot?.backgroundRadius != null
      )
    : false;
  if (
    node.style?.fontSize != null ||
    node.style?.letterSpacing != null ||
    node.style?.backgroundPadding != null ||
    node.style?.backgroundRadius != null ||
    hasHighlightMetrics
  ) {
    next.style = { ...node.style };
    if (node.style?.fontSize != null) {
      next.style.fontSize = Math.max(
        1,
        scalePx({ value: node.style.fontSize, scale })
      );
    }
    if (node.style?.letterSpacing != null) {
      const spacing = Number(node.style.letterSpacing);
      next.style.letterSpacing = Number.isFinite(spacing)
        ? scalePx({ value: spacing, scale })
        : node.style.letterSpacing;
    }
    if (node.style?.backgroundPadding != null) {
      const padding = Number(node.style.backgroundPadding);
      next.style.backgroundPadding = Number.isFinite(padding)
        ? scalePx({ value: padding, scale })
        : node.style.backgroundPadding;
    }
    if (node.style?.backgroundRadius != null) {
      const radius = Number(node.style.backgroundRadius);
      next.style.backgroundRadius = Number.isFinite(radius)
        ? scalePx({ value: radius, scale })
        : node.style.backgroundRadius;
    }
    if (Array.isArray(node.style?.highlights) && node.style.highlights.length) {
      next.style.highlights = scaleHighlightMetrics({
        highlights: node.style.highlights,
        scale,
      });
    }
  }
  if (node.shape?.radius != null) {
    next.shape = {
      ...node.shape,
      radius: scalePx({ value: node.shape.radius, scale }),
    };
  }
  if (node.componentFrame) {
    next.componentFrame = {
      ...node.componentFrame,
      width: scalePx({ value: node.componentFrame.width, scale }),
      height: scalePx({ value: node.componentFrame.height, scale }),
    };
  }
  if (node.overflow?.minimumFontSize != null) {
    next.overflow = {
      ...node.overflow,
      minimumFontSize: Math.max(
        1,
        scalePx({ value: node.overflow.minimumFontSize, scale })
      ),
    };
  }
  return next;
};

export const scaleNodeLayouts = ({ nodeLayouts = {}, from, to }) => {
  const { scale, offsetX, offsetY } = getCanvasFitTransform({ from, to });
  const next = {};
  Object.entries(nodeLayouts || {}).forEach(([id, layout]) => {
    next[id] = scaleLayout({ layout, scale, offsetX, offsetY });
  });
  return next;
};

const scaleNodesTreeWithTransform = ({
  nodes = {},
  scale,
  offsetX = 0,
  offsetY = 0,
}) => {
  const next = {};
  Object.entries(nodes || {}).forEach(([id, node]) => {
    if (!node) return;
    const scaled = scaleNodeMetrics({ node, scale });
    if (node.layout) {
      scaled.layout = scaleLayout({
        layout: node.layout,
        scale,
        offsetX,
        offsetY,
      });
    } else {
      delete scaled.layout;
    }
    if (node.children) {
      scaled.children = scaleNodesTreeWithTransform({
        nodes: node.children,
        scale,
        offsetX: 0,
        offsetY: 0,
      });
    } else {
      delete scaled.children;
    }
    next[id] = scaled;
  });
  return next;
};

export const scaleNodesTree = ({ nodes = {}, from, to }) => {
  const { scale, offsetX, offsetY } = getCanvasFitTransform({ from, to });
  return scaleNodesTreeWithTransform({
    nodes,
    scale,
    offsetX,
    offsetY,
  });
};

export const getArtboardNodes = ({ artboard, fallbackNodes = {} }) =>
  artboard?.nodes != null ? artboard.nodes : fallbackNodes;

const pinCanvasBackgrounds = ({ artboard } = {}) => {
  const nodes = artboard?.nodes || {};
  let nodeLayouts = artboard?.nodeLayouts || {};
  const nextNodes = { ...nodes };
  Object.values(nodes).forEach((node) => {
    if (!isCanvasBackgroundNode({ node, parentID: null })) return;
    const fit = canvasBackgroundFit(node);
    if (node.fit !== fit) {
      nextNodes[node.id] = { ...node, fit };
    }
    nodeLayouts = applyCanvasBackgroundLayout({
      nodeLayouts,
      nodeID: node.id,
      width: artboard.width,
      height: artboard.height,
    });
  });
  return { nodes: nextNodes, nodeLayouts };
};

export const hydrateArtboards = ({
  artboards = {},
  nodes: sharedNodes = {},
  allowSharedNodes = true,
}) => {
  const hasAnyArtboardNodes = Object.values(artboards || {}).some(
    (artboard) => artboard?.nodes != null
  );
  const fallbackNodes =
    allowSharedNodes && !hasAnyArtboardNodes ? cloneTree(sharedNodes) : {};
  const next = {};
  Object.entries(artboards || {}).forEach(([id, artboard]) => {
    const nodes = getArtboardNodes({ artboard, fallbackNodes });
    const pinned = pinCanvasBackgrounds({
      artboard: { ...artboard, nodes },
    });
    next[id] = {
      ...artboard,
      nodes: pinned.nodes,
      nodeLayouts: pinned.nodeLayouts,
    };
  });
  return next;
};

export const copySourceGraphIntoEmptyArtboards = ({
  artboards = {},
  sourceArtboards = {},
} = {}) => {
  const next = { ...artboards };
  Object.entries(sourceArtboards || {}).forEach(([id, source]) => {
    const current = next[id];
    if (!current || !source) return;
    const hasNodes = countNodes({ nodes: current.nodes || {} }) > 0;
    const hasLayouts = Object.keys(current.nodeLayouts || {}).length > 0;
    if (hasNodes && hasLayouts) return;
    next[id] = {
      ...current,
      ...(!hasNodes && source.nodes ? { nodes: source.nodes } : {}),
      ...(!hasLayouts && source.nodeLayouts
        ? { nodeLayouts: source.nodeLayouts }
        : {}),
    };
  });
  return next;
};

export const createEmptyArtboard = ({ width, height, label }) => ({
  label,
  width: Math.round(Number(width) || 0),
  height: Math.round(Number(height) || 0),
  nodes: {},
  nodeLayouts: {},
});

export const nextCustomArtboardID = ({ width, height, artboards = {} }) => {
  const base = `custom_${Math.round(Number(width) || 0)}x${Math.round(Number(height) || 0)}`;
  if (!artboards[base]) return base;
  let suffix = 2;
  while (artboards[`${base}_${suffix}`]) suffix += 1;
  return `${base}_${suffix}`;
};

const aspectDistance = ({ from, toWidth, toHeight }) => {
  const fromRatio = getArtboardRatio({
    width: from?.width,
    height: from?.height,
  });
  const toRatio = getArtboardRatio({ width: toWidth, height: toHeight });
  if (!fromRatio || !toRatio) return Infinity;
  return Math.abs(Math.log(fromRatio) - Math.log(toRatio));
};

export const findCopySourceArtboard = ({
  artboards = {},
  excludeID,
  preferredID,
  width,
  height,
} = {}) => {
  const hasContent = (artboard) =>
    countNodes({ nodes: artboard?.nodes || {} }) > 0;
  const candidates = Object.entries(artboards).filter(
    ([id, artboard]) => id !== excludeID && hasContent(artboard)
  );
  if (!candidates.length) return null;
  if (!width || !height) {
    const preferred = candidates.find(([id]) => id === preferredID);
    return (preferred || candidates[0])[1];
  }
  candidates.sort(([leftID, left], [rightID, right]) => {
    const diff =
      aspectDistance({ from: left, toWidth: width, toHeight: height }) -
      aspectDistance({ from: right, toWidth: width, toHeight: height });
    if (Math.abs(diff) > 1e-6) return diff;
    if (leftID === preferredID) return -1;
    if (rightID === preferredID) return 1;
    return 0;
  });
  return candidates[0][1];
};

const BACKGROUND_FILL_EDGE = 0.9;

const layoutFillsCanvas = ({ layout, width, height }) => {
  const x = Number(layout?.x) || 0;
  const y = Number(layout?.y) || 0;
  const boxWidth = Number(layout?.width) || 0;
  const boxHeight = Number(layout?.height) || 0;
  const slackX = width * (1 - BACKGROUND_FILL_EDGE);
  const slackY = height * (1 - BACKGROUND_FILL_EDGE);
  return (
    x <= slackX &&
    y <= slackY &&
    x + boxWidth >= width * BACKGROUND_FILL_EDGE &&
    y + boxHeight >= height * BACKGROUND_FILL_EDGE
  );
};

// A full-bleed photo often has no isBackground flag. The largest root image
// that still sits behind the other large images is the unmarked background.
export const inferImplicitCanvasBackgroundNodeID = ({
  nodes = {},
  nodeLayouts = {},
  width,
  height,
} = {}) => {
  const canvasWidth = Number(width) || 0;
  const canvasHeight = Number(height) || 0;
  if (!canvasWidth || !canvasHeight) return null;

  const roots = Object.values(nodes).filter(Boolean);
  if (roots.some((node) => isCanvasBackgroundNode({ node, parentID: null }))) {
    return null;
  }

  const candidates = roots
    .filter(
      (node) =>
        canBeCanvasBackground({ node, parentID: null }) &&
        nodeLayouts[node.id]?.visible !== false
    )
    .map((node) => {
      const layout = nodeLayouts[node.id] || {};
      return {
        id: node.id,
        area: (Number(layout.width) || 0) * (Number(layout.height) || 0),
        zIndex: Number(layout.zIndex) || 0,
        fills: layoutFillsCanvas({
          layout,
          width: canvasWidth,
          height: canvasHeight,
        }),
      };
    })
    .filter((item) => item.fills && item.area > 0);

  if (!candidates.length) return null;

  const maxArea = Math.max(...candidates.map((item) => item.area));
  const largest = candidates.filter((item) => item.area >= maxArea * 0.9);
  largest.sort((left, right) => {
    if (left.zIndex !== right.zIndex) return left.zIndex - right.zIndex;
    return right.area - left.area;
  });
  return largest[0]?.id || null;
};

export const seedArtboard = ({ fromArtboard, preset }) => {
  const nodes = scaleNodesTree({
    nodes: cloneTree(fromArtboard?.nodes || {}),
    from: fromArtboard,
    to: preset,
  });
  let nodeLayouts = scaleNodeLayouts({
    nodeLayouts: fromArtboard?.nodeLayouts || {},
    from: fromArtboard,
    to: preset,
  });
  Object.values(nodes).forEach((node) => {
    if (!isCanvasBackgroundNode({ node, parentID: null })) return;
    nodeLayouts = applyCanvasBackgroundLayout({
      nodeLayouts,
      nodeID: node.id,
      width: preset.width,
      height: preset.height,
    });
  });
  return {
    label: preset.label,
    width: preset.width,
    height: preset.height,
    nodes,
    nodeLayouts,
  };
};

export const applyCanvasBackgroundToArtboards = ({
  artboards = {},
  nodeID,
  isBackground,
  clearCrop = false,
}) => {
  const next = {};
  Object.entries(artboards).forEach(([artboardID, artboard]) => {
    const nodes = artboard?.nodes || {};
    if (!nodes[nodeID]) {
      next[artboardID] = artboard;
      return;
    }
    const backgroundNodes = setRootCanvasBackground({
      nodes,
      nodeID,
      isBackground,
    });
    let nextNodes = backgroundNodes;
    if (clearCrop && isBackground && backgroundNodes[nodeID]?.crop != null) {
      const withoutCrop = { ...backgroundNodes[nodeID] };
      delete withoutCrop.crop;
      nextNodes = { ...backgroundNodes, [nodeID]: withoutCrop };
    }
    const nextNode = nextNodes[nodeID];
    const shouldFillLayout =
      isBackground &&
      isCanvasBackgroundNode({ node: nextNode, parentID: null });
    next[artboardID] = {
      ...artboard,
      nodes: nextNodes,
      nodeLayouts: shouldFillLayout
        ? applyCanvasBackgroundLayout({
            nodeLayouts: artboard.nodeLayouts || {},
            nodeID,
            width: artboard.width,
            height: artboard.height,
          })
        : artboard.nodeLayouts,
    };
  });
  return next;
};

export const applySourceKindToArtboards = ({ artboards = {}, name, kind }) => {
  const next = {};
  Object.entries(artboards).forEach(([artboardID, artboard]) => {
    const nodes = artboard?.nodes || {};
    next[artboardID] = {
      ...artboard,
      nodes: applySourceKindByName({ nodes, name, kind }),
    };
  });
  return next;
};

export const patchActiveArtboardGraph = ({
  artboards = {},
  artboardID,
  nodes,
  nodeLayouts,
}) => {
  const current = artboards[artboardID] || {};
  return {
    ...artboards,
    [artboardID]: {
      ...current,
      ...(nodes !== undefined ? { nodes } : {}),
      ...(nodeLayouts !== undefined ? { nodeLayouts } : {}),
    },
  };
};

export const countAllArtboardNodes = ({ artboards = {}, nodes } = {}) => {
  const fromArtboards = Object.values(artboards || {}).reduce(
    (total, artboard) => total + countNodes({ nodes: artboard?.nodes || {} }),
    0
  );
  if (fromArtboards > 0) return fromArtboards;
  return countNodes({ nodes: nodes || {} });
};

export const getArtboardReference = ({
  artboard,
  artboardID,
  assets = {},
  allowLegacyReference = true,
}) => {
  const refKey = artboard?.referenceAssetRef;
  if (refKey && assets[refKey]) return assets[refKey];
  if (
    allowLegacyReference &&
    !refKey &&
    artboardID === CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID &&
    assets.reference
  ) {
    return assets.reference;
  }
  return null;
};

export const shouldShowReferenceBackdrop = ({
  artboard,
  artboardID,
  assets = {},
  reference,
  allowLegacyReference = true,
}) =>
  Boolean(
    getArtboardReference({
      artboard,
      artboardID,
      assets,
      allowLegacyReference,
    })?.url ||
      (allowLegacyReference &&
        reference?.url &&
        Number(artboard?.width) === Number(reference.width) &&
        Number(artboard?.height) === Number(reference.height))
  );

export const listBackdropAssets = ({
  assets = {},
  artboards = {},
  slides = [],
} = {}) => {
  const keys = new Set();
  Object.keys(assets || {}).forEach((key) => {
    if (key === "reference" || key.startsWith("reference_")) keys.add(key);
  });
  Object.values(artboards || {}).forEach((artboard) => {
    if (artboard?.referenceAssetRef) keys.add(artboard.referenceAssetRef);
    Object.values(artboard?.slideGuides || {}).forEach((key) => {
      if (key) keys.add(key);
    });
  });
  (slides || []).forEach((slide) => {
    Object.values(slide?.artboards || {}).forEach((artboard) => {
      if (artboard?.referenceAssetRef) keys.add(artboard.referenceAssetRef);
    });
  });
  return [...keys]
    .map((key) => ({ key, asset: assets[key] }))
    .filter((entry) => entry.asset?.url);
};

export const findReferenceArtboardID = ({
  artboards = {},
  reference,
  assets = {},
}) => {
  const matchedByRef = Object.entries(artboards).find(([, artboard]) => {
    const refKey = artboard?.referenceAssetRef;
    return (
      refKey &&
      assets[refKey]?.assetID &&
      assets[refKey].assetID === reference?.assetID
    );
  });
  if (matchedByRef) return matchedByRef[0];
  const matched = Object.entries(artboards).find(([id, artboard]) =>
    shouldShowReferenceBackdrop({
      artboard,
      artboardID: id,
      assets,
      reference,
    })
  );
  if (matched) return matched[0];
  return resolveActiveArtboardID({
    artboards,
    activeArtboardID: CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID,
  });
};

export const getCardArtboardFields = ({ artboards = {}, activeArtboardID }) => {
  const resolvedID = resolveActiveArtboardID({ artboards, activeArtboardID });
  const artboard = artboards[resolvedID] || {};
  return {
    artboard: {
      id: resolvedID,
      width: artboard.width || CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_SIZE,
      height: artboard.height || CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_SIZE,
    },
    artboardIDs: listArtboardIDs({ artboards }),
    artboardFormats: listUniqueArtboardFormatLabels({ artboards }),
  };
};
